// Iter 82.1 / Phase E1.1 — Leave Balances dashboard.
// Lists all materialised leave balances with filters by FY, Department,
// Leave Type, and Employee search. Drills into a per-employee detail
// page for ledger drill-down and adjustments/encashments.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DownloadSimple, ArrowRight, FunnelSimple, ArrowsClockwise, Warning } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import LeaveAdminSetupChecklist from "../../components/leave/LeaveAdminSetupChecklist";  // Iter 110 · Setup wizard
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

export default function LeaveBalances() {
    const { can } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [fys, setFys] = useState([]);
    const [hrMeta, setHrMeta] = useState({ departments: [] });
    const [policies, setPolicies] = useState([]);
    // UAT · Opening Balance Provisioning fix — Migration Mode detection
    // only used to increase visual prominence, not to gate the action.
    const [migrationActive, setMigrationActive] = useState(false);
    const [includePending, setIncludePending] = useState(
        // Deep-link support — /admin/leave/balances?include_pending=1 or
        // ?state=pending_opening auto-toggles the pending enumeration on.
        searchParams.get("include_pending") === "1"
        || searchParams.get("state") === "pending_opening"
    );
    const [pendingCount, setPendingCount] = useState(0);
    const [filters, setFilters] = useState({
        fiscal_year: "",
        department: "",
        leave_type_code: "",
        search: "",
        state: searchParams.get("state") || "",
    });
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rebuilding, setRebuilding] = useState(false);

    const loadMeta = useCallback(async () => {
        try {
            const [fy, hm, pl, mm] = await Promise.all([
                api.get("/leave/fiscal-year/list"),
                api.get("/hr/employee/meta"),
                api.get("/leave/policy/list"),
                // UAT · Opening Balance Provisioning fix — Migration Mode
                // status is READ-ONLY here; used to visually promote the
                // pending panel. Do NOT gate any action on this.
                api.get("/billing/migration-mode/status").catch(() => ({ data: { state: "not_started" } })),
            ]);
            setFys(fy.data.items || []);
            setHrMeta(hm.data);
            setPolicies(pl.data.items || []);
            setMigrationActive(mm.data?.state === "active");
            // If Migration Mode is active, auto-enable the pending toggle
            // on first-load only (unless the operator explicitly opted out
            // via query param). Post-load, operator's toggle is authoritative.
            if (mm.data?.state === "active" && searchParams.get("include_pending") === null
                && searchParams.get("state") === null) {
                setIncludePending(true);
            }
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load filter metadata"));
        }
    }, [searchParams]);

    const reload = useCallback(async (f) => {
        setLoading(true);
        try {
            const params = {};
            if (f.fiscal_year) params.fiscal_year = f.fiscal_year;
            if (f.department) params.department = f.department;
            if (f.leave_type_code) params.leave_type_code = f.leave_type_code;
            if (f.search) params.search = f.search;
            if (f.state) params.state = f.state;
            // UAT · Opening Balance Provisioning fix — read-contract
            // extension. Backend enumerates (emp × applicable policy)
            // combinations with no materialised balance when this flag
            // is true. Existing callers omit it and see legacy shape.
            if (includePending) params.include_pending = true;
            const { data } = await api.get("/leave/balance/list", { params });
            setRows(data.items || []);
            setPendingCount(data.pending_opening_count || 0);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load leave balances"));
        } finally {
            setLoading(false);
        }
    }, [includePending]);

    useEffect(() => { Promise.resolve().then(loadMeta); }, [loadMeta]);

    // Derive an effective FY: explicit filter takes precedence; else the
    // current FY from the seeded list. We don't set this into state to
    // satisfy the React 19 set-state-in-effect lint.
    const effectiveFy = useMemo(() => {
        if (filters.fiscal_year) return filters.fiscal_year;
        const current = (fys || []).find((f) => f.is_current);
        return current?.code || "";
    }, [filters.fiscal_year, fys]);

    useEffect(() => {
        if (effectiveFy) reload({ ...filters, fiscal_year: effectiveFy });
    }, [filters, effectiveFy, reload]);

    const filtered = useMemo(() => {
        if (!filters.search) return rows;
        const term = filters.search.toLowerCase();
        return rows.filter((r) => {
            const emp = r.employee || {};
            return (
                (emp.employee_code || "").toLowerCase().includes(term) ||
                (emp.full_name || "").toLowerCase().includes(term)
            );
        });
    }, [rows, filters.search]);

    const exportCsv = async () => {
        try {
            const params = {};
            if (filters.fiscal_year) params.fiscal_year = filters.fiscal_year;
            if (filters.department) params.department = filters.department;
            if (filters.leave_type_code) params.leave_type_code = filters.leave_type_code;
            const { data } = await api.get("/leave/balance/export", { params, responseType: "blob" });
            const url = URL.createObjectURL(data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `leave-balances-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err, "CSV export failed"));
        }
    };

    const rebuild = async () => {
        if (!window.confirm("Recompute materialised balances from the ledger? This is safe but may take a few seconds on large datasets.")) return;
        setRebuilding(true);
        try {
            const { data } = await api.post("/leave/balance/rebuild");
            toast.success(`Rebuilt ${data.rows_rebuilt} balance row(s)`);
            reload(filters);
        } catch (err) {
            toast.error(formatApiError(err, "Rebuild failed"));
        } finally {
            setRebuilding(false);
        }
    };

    const leaveCodes = useMemo(() => {
        const codes = new Set((policies || []).map((p) => p.code));
        rows.forEach((r) => codes.add(r.leave_type_code));
        return Array.from(codes).sort();
    }, [policies, rows]);

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="leave-balances-page">
            <PageHeader
                eyebrow="Leave Operations"
                title="Leave Balances"
                description="Materialised view of every employee's current leave balance per fiscal year. Drill into a row to see ledger history and post adjustments."
                actions={(
                    <div className="flex gap-2">
                        {can("leave:admin") && (
                            <Button variant="outline" size="sm" onClick={rebuild} disabled={rebuilding} data-testid="leave-balances-rebuild-btn">
                                <ArrowsClockwise size={14} className="mr-1" /> {rebuilding ? "Rebuilding…" : "Rebuild from Ledger"}
                            </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={exportCsv} data-testid="leave-balances-export-btn">
                            <DownloadSimple size={14} className="mr-1" /> Export CSV
                        </Button>
                    </div>
                )}
            />

            <div className="mt-5">
                <LeaveAdminSetupChecklist
                    variant="banner"
                    hideWhenComplete={!loading && rows.length > 0}
                />
            </div>

            {can("leave:admin") && (
                <section
                    className={
                        "mt-5 rounded-xl border p-4 flex items-center flex-wrap gap-3 " +
                        (migrationActive
                            ? "bg-amber-50 border-amber-300 shadow-sm"
                            : "bg-slate-50 border-slate-200")
                    }
                    data-testid="lb-pending-toggle-section"
                >
                    <div className="flex items-center gap-2">
                        <Warning
                            size={18}
                            className={migrationActive ? "text-amber-700" : "text-slate-500"}
                        />
                        <div>
                            <div className="text-sm font-semibold text-slate-900">
                                Credit Opening Balances
                                {migrationActive && (
                                    <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">
                                        Migration Mode active
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-slate-600 mt-0.5">
                                Show employees with mapped policies but no opening balance yet
                                {pendingCount > 0 && (
                                    <> · <span className="font-semibold text-amber-800">{pendingCount} pending</span></>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="grow" />
                    <label
                        className="inline-flex items-center gap-2 text-sm cursor-pointer select-none"
                        data-testid="lb-pending-toggle-label"
                    >
                        <input
                            type="checkbox"
                            checked={includePending}
                            onChange={(e) => {
                                const next = e.target.checked;
                                setIncludePending(next);
                                const p = new URLSearchParams(searchParams);
                                if (next) p.set("include_pending", "1");
                                else { p.delete("include_pending"); p.delete("state"); }
                                setSearchParams(p, { replace: true });
                            }}
                            className="w-4 h-4"
                            data-testid="lb-pending-toggle"
                        />
                        <span>Include pending opening credits</span>
                    </label>
                </section>
            )}

            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="leave-balances-filters">
                <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700">
                    <FunnelSimple size={14} /> Filters
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                        <label className="text-xs text-slate-600">Fiscal Year</label>
                        <select
                            data-testid="lb-filter-fy"
                            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={effectiveFy}
                            onChange={(e) => setFilters({ ...filters, fiscal_year: e.target.value })}
                        >
                            {fys.map((fy) => (
                                <option key={fy.id} value={fy.code}>{fy.code}{fy.is_current ? " (current)" : ""}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Department</label>
                        <select
                            data-testid="lb-filter-dept"
                            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.department}
                            onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                        >
                            <option value="">All departments</option>
                            {(hrMeta.departments || []).map((d) => (
                                <option key={d.id || d.name} value={d.name}>{d.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Leave Type</label>
                        <select
                            data-testid="lb-filter-type"
                            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.leave_type_code}
                            onChange={(e) => setFilters({ ...filters, leave_type_code: e.target.value })}
                        >
                            <option value="">All leave types</option>
                            {leaveCodes.map((c) => (<option key={c} value={c}>{c}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Search</label>
                        <Input
                            data-testid="lb-filter-search"
                            placeholder="Employee code or name"
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            className="mt-1"
                        />
                    </div>
                </div>
            </section>

            <section className="mt-5 bg-white rounded-xl border border-slate-200" data-testid="leave-balances-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Department</th>
                                <th className="text-left px-3 py-2 font-medium">Leave Type</th>
                                <th className="text-right px-3 py-2 font-medium">Current</th>
                                <th className="text-right px-3 py-2 font-medium">Opening</th>
                                <th className="text-right px-3 py-2 font-medium">Accrued YTD</th>
                                <th className="text-right px-3 py-2 font-medium">Taken YTD</th>
                                <th className="text-right px-3 py-2 font-medium">Encashed</th>
                                <th className="text-right px-3 py-2 font-medium">Adjusted</th>
                                <th className="text-left px-3 py-2 font-medium">Last Movement</th>
                                <th className="w-12" />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={11} className="text-center py-10 text-slate-400">Loading…</td></tr>
                            )}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan={11} className="text-center py-10 text-slate-400">No leave balances found for the current filters.</td></tr>
                            )}
                            {!loading && filtered.map((r) => {
                                const emp = r.employee || {};
                                const isPending = r.state === "pending_opening";
                                return (
                                    <tr
                                        key={`${r.employee_id}-${r.leave_type_code}-${r.fiscal_year}-${r.state || "m"}`}
                                        className={
                                            "border-t border-slate-100 cursor-pointer transition-colors "
                                            + (isPending
                                                ? "bg-amber-50/50 hover:bg-amber-100/70 border-l-4 border-l-amber-400"
                                                : "hover:bg-slate-50")
                                        }
                                        onClick={() => navigate(
                                            `/admin/leave/balances/${r.employee_id}?fy=${r.fiscal_year}`
                                            + (isPending ? `&focus_pending=${r.leave_type_code}` : "")
                                        )}
                                        data-testid={`lb-row-${r.employee_id}-${r.leave_type_code}${isPending ? "-pending" : ""}`}
                                    >
                                        <td className="px-3 py-2">
                                            <div className="font-medium text-slate-900 flex items-center gap-1.5">
                                                {emp.full_name || "—"}
                                                {isPending && (
                                                    <span
                                                        className="text-[10px] font-bold uppercase tracking-wider bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded"
                                                        data-testid={`lb-row-pending-badge-${r.employee_id}-${r.leave_type_code}`}
                                                    >
                                                        Pending
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-slate-500">{emp.employee_code || r.employee_id}</div>
                                        </td>
                                        <td className="px-3 py-2 text-slate-600">{emp.department || "—"}</td>
                                        <td className="px-3 py-2 font-mono text-xs">
                                            {r.leave_type_code}
                                            {isPending && r.policy?.name && (
                                                <div className="text-[10px] font-sans text-slate-500 mt-0.5">{r.policy.name}</div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right font-semibold text-slate-900">
                                            {isPending ? <span className="text-slate-400 italic text-xs">no balance yet</span> : (r.current_balance ?? 0).toFixed(2)}
                                        </td>
                                        <td className="px-3 py-2 text-right text-slate-600">{isPending ? "—" : (r.opening_balance ?? 0).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right text-emerald-700">{isPending ? "—" : (r.total_accrued_ytd ?? 0).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right text-rose-700">{isPending ? "—" : (r.total_taken_ytd ?? 0).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right text-amber-700">{isPending ? "—" : (r.total_encashed_ytd ?? 0).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right text-slate-600">{isPending ? "—" : (r.total_adjusted_ytd ?? 0).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-xs text-slate-500">
                                            {isPending ? (
                                                <span className="text-amber-800 font-semibold">Awaiting HR credit</span>
                                            ) : r.last_movement_at ? (
                                                <>
                                                    <div>{formatDate(r.last_movement_at)}</div>
                                                    <div className="text-slate-400">{r.last_movement_type}</div>
                                                </>
                                            ) : "—"}
                                        </td>
                                        <td className="px-3 py-2 text-slate-400">
                                            {isPending ? (
                                                <span
                                                    className="inline-flex items-center gap-1 text-amber-700 font-semibold text-xs"
                                                    data-testid={`lb-row-credit-cta-${r.employee_id}-${r.leave_type_code}`}
                                                >
                                                    Credit <ArrowRight size={12} />
                                                </span>
                                            ) : <ArrowRight size={14} />}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            <div className="mt-3 text-xs text-slate-500" data-testid="lb-rowcount">
                {loading ? "" : `${filtered.length} of ${rows.length} balance rows`}
            </div>
        </div>
    );
}
